/**
 * Dauerhafter Zwischenspeicher für gezeichnete Grafiken (TikZ, Mermaid).
 *
 * Beides ist teuer: TikZ geht übers Netz, Mermaid rechnet im Browser. Am
 * Ergebnis ändert sich aber nur etwas, wenn sich der Quelltext ändert –
 * deshalb ist der Schlüssel genau dieser Quelltext (als Hash) plus das
 * Farbschema. Ein Neuladen der Seite zeichnet damit nichts neu.
 *
 * `localStorage` reicht dafür nicht: es fasst rund 5 MB für alles zusammen,
 * und ein einziges TikZ-Bild kann mehrere hundert Kilobyte groß sein. Also
 * IndexedDB, mit einer kleinen Schicht im Arbeitsspeicher davor, damit das
 * Zeichnen beim Tippen nicht auf die Datenbank warten muss.
 */

const DB_NAME = "lernapp-svg";
const STORE = "svg";
/** So viele Grafiken bleiben liegen; darüber fliegen die ältesten raus. */
const KEEP = 300;

const memory = new Map<string, string>();
let ready: Promise<IDBDatabase | null> | null = null;

function db(): Promise<IDBDatabase | null> {
  ready ??= new Promise((resolve) => {
    try {
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(STORE)) {
          database.createObjectStore(STORE, { keyPath: "key" });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => {
        console.warn("[svgCache] indexedDB.open fehlgeschlagen", request.error);
        resolve(null);
      };
    } catch (err) {
      console.warn("[svgCache] indexedDB.open geworfen", err);
      resolve(null);
    }
  });
  return ready;
}

function run<T>(
  database: IDBDatabase,
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T | null> {
  return new Promise((resolve) => {
    try {
      const request = action(database.transaction(STORE, mode).objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => {
        console.warn("[svgCache] Transaktion fehlgeschlagen", request.error);
        resolve(null);
      };
    } catch (err) {
      console.warn("[svgCache] Transaktion geworfen", err);
      resolve(null);
    }
  });
}

/** Sofortiger Treffer aus dem Arbeitsspeicher – ohne Warten. */
export function svgCached(key: string): string | null {
  return memory.get(key) ?? null;
}

/** Treffer aus der Datenbank; füllt dabei den Arbeitsspeicher. */
export async function svgLoad(key: string): Promise<string | null> {
  const hit = memory.get(key);
  if (hit) return hit;
  const database = await db();
  if (!database) return null;
  const row = await run<{ svg?: string }>(database, "readonly", (store) => store.get(key));
  if (!row?.svg) return null;
  memory.set(key, row.svg);
  return row.svg;
}

export async function svgStore(key: string, svg: string): Promise<void> {
  memory.set(key, svg);
  const database = await db();
  if (!database) return;
  await run(database, "readwrite", (store) => store.put({ key, svg, touched: Date.now() }));

  const keys = (await run<{ key: string; touched: number }[]>(database, "readonly", (store) =>
    store.getAll()
  )) ?? [];
  if (keys.length <= KEEP) return;
  for (const row of keys.sort((a, b) => b.touched - a.touched).slice(KEEP)) {
    await run(database, "readwrite", (store) => store.delete(row.key));
  }
}

/**
 * Alle gespeicherten Grafiken in den Arbeitsspeicher holen. Einmal beim
 * Öffnen einer Notiz aufgerufen, steht danach jeder Treffer sofort bereit –
 * ohne das kurze Aufblitzen von „wird gezeichnet".
 */
export async function svgWarmUp(): Promise<void> {
  const database = await db();
  if (!database) return;
  const rows = (await run<{ key: string; svg: string }[]>(database, "readonly", (store) =>
    store.getAll()
  )) ?? [];
  for (const row of rows) if (row.key && row.svg) memory.set(row.key, row.svg);
}
